import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ValidationService {
  static required(value: string | null): boolean {
    return !!value?.trim();
  }

  static minLength(value: string | null, minLength: number): boolean {
    return value ? value.length >= minLength : false;
  }

  static maxLength(value: string | null, maxLength: number): boolean {
    return value ? value.length <= maxLength : false;
  }

  static fixedLength(value: string | null, fixedLength: number): boolean {
    return value ? value.length == fixedLength : false;
  }

  static email(value: string | null): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return value ? emailPattern.test(value) : false;
  }

  static isWholeNumber(value: string | null): boolean {
    const wholeNumberPattern = /^[+-]?\d+$/;
    return value ? wholeNumberPattern.test(value) : false;
  }

  static isAlphabetic(value: string | null): boolean {
    const pricePattern = /^\d*\.?\d*$/;
    return value ? pricePattern.test(value) : false;
  }

  static notContainsSpecialCharOrNumber(value: string | null): boolean {
    const specialCharOrNumberPattern = /[0-9!@#$%^&*(),.?":{}|<>_\-+=~`]/;
    return value ? !specialCharOrNumberPattern.test(value) : false;
  }

  // Generic address validation method
  private addressRegex = /^[a-zA-Z0-9\s,'\-#\.]{3,}$/;
  private nameRegex = /^[a-zA-Z\s]+$/; // letters + spaces only
  private zipCodeRegex = /^\d+$/; // digits only

  // Line1: required, basic address characters
  validateAddressLine1(line1: string | null): {
    valid: boolean;
    message: string;
  } {
    if (!line1?.trim()) {
      return { valid: false, message: 'Address Line 1 is required.' };
    }

    if (!this.addressRegex.test(line1.trim())) {
      return {
        valid: false,
        message:
          'Address Line 1 contains invalid characters or is too short (minimum 3 characters).',
      };
    }

    return { valid: true, message: '' };
  }

  // Line2: optional, but if provided must be valid
  validateAddressLine2(line2: string | null): {
    valid: boolean;
    message: string;
  } {
    if (!line2?.trim()) {
      return { valid: true, message: '' };
    }

    if (!this.addressRegex.test(line2.trim())) {
      return {
        valid: false,
        message:
          'Address Line 2 contains invalid characters or is too short (minimum 3 characters).',
      };
    }

    return { valid: true, message: '' };
  }

  // CityName: required, letters + spaces only
  validateCityName(city: string | null): { valid: boolean; message: string } {
    if (!city?.trim()) {
      return { valid: false, message: 'City name is required.' };
    }

    if (!this.nameRegex.test(city.trim())) {
      return {
        valid: false,
        message: 'Numbers or special characters are not allowed in city name.',
      };
    }

    return { valid: true, message: '' };
  }

  // StateName: required, letters + spaces only
  validateStateName(state: string | null): { valid: boolean; message: string } {
    if (!state?.trim()) {
      return { valid: false, message: 'State name is required.' };
    }

    if (!this.nameRegex.test(state.trim())) {
      return {
        valid: false,
        message: 'Numbers or special characters are not allowed in state name.',
      };
    }

    return { valid: true, message: '' };
  }

  // CountryName: required, letters + spaces only
  validateCountryName(country: string | null): {
    valid: boolean;
    message: string;
  } {
    if (!country?.trim()) {
      return { valid: false, message: 'Country name is required.' };
    }

    if (!this.nameRegex.test(country.trim())) {
      return {
        valid: false,
        message: 'Numbers or special characters are not allowed in country name.',
      };
    }

    return { valid: true, message: '' };
  }

  // ZipCode: required, digits only, no spaces/special characters
  validateZipCode(zip: string | null): { valid: boolean; message: string } {
    if (!zip?.trim()) {
      return { valid: false, message: 'ZIP code is required.' };
    }

    if (!this.zipCodeRegex.test(zip.trim())) {
      return {
        valid: false,
        message: 'Enter a valid ZIP code (only digits are allowed).',
      };
    }

    return { valid: true, message: '' };
  }
}
